import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import { noteActiveDay } from "@/lib/activeDay";

/**
 * Mounted once globally inside <AuthProvider>. Pings community_touch_presence
 * every ~60s while the tab is visible AND the user is signed in, regardless
 * of which page they're on. This makes `community_user_state.last_seen_at`
 * reflect "last time the member actually had the site open in their browser",
 * not just "last time they typed their password" (which Supabase keeps stale
 * because sessions persist for ~1 year).
 *
 * It also notes the day for the admin dashboard's active days (src/lib/activeDay.ts):
 * on load, when the tab comes back, and every 10 minutes, so a tab left open past
 * midnight counts the new day.
 */
export function GlobalPresence() {
  const { user, session } = useAuth();
  usePresenceHeartbeat(!!user);
  const token = session?.access_token, id = user?.id;
  useEffect(() => {
    if (!token || !id) return;
    const note = () => noteActiveDay(token, id);
    note();
    const t = setInterval(note, 10 * 60_000);
    document.addEventListener("visibilitychange", note);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", note); };
  }, [token, id]);
  return null;
}
